<template>
	<v-main class="reservation-screen-view mt-12 mt-md-16">
		<v-dialog 
			max-width="800"
			v-model="transportConditionsDialogVisible"
			:fullscreen="$vuetify.display.mobile"
			:scrollable="true"
			no-click-animation
		>
			<v-card>
				<v-btn flat icon color="transparent" size="x-small" class="close" @click="transportConditionsDialogVisible = false">
					<v-icon color="primary" size="24" icon="mdi-close"></v-icon>
				</v-btn>
				<transport-conditions-component/>
			</v-card>
		</v-dialog>
		<v-dialog 
			max-width="900"
			v-model="legalInformationDialogVisible"
			:fullscreen="$vuetify.display.mobile"
			:scrollable="true"
			no-click-animation
		>
			<v-card>
				<v-btn flat icon color="transparent" size="x-small" class="close" @click="legalInformationDialogVisible = false">
					<v-icon color="primary" size="24" icon="mdi-close"></v-icon>
				</v-btn>
				<legal-component/>
			</v-card>
		</v-dialog>
		<chained-dialog-component
			:slotCount="3"
			:currentSlot="currentSlot"
			:closeIcon="close_icon">
			<template v-slot:slot_1>
				<v-row>
					<v-col cols="12" md="10" offset-md="1">
						<div class="reservation-screen-header text-left pl-6 pl-md-1" v-if="!loading_save">
							{{$t('reservation.main_header')}}
						</div>
					</v-col>
				</v-row>
				<div class="d-flex flex-md-row flex-column mt-8 justify-center" v-if="!loading_save">
					<div class="invicta">

						<TabGroup
							:tabSettings="tabGroupSettings"
							:selectedName="'tab_people'"
							class="flex-grow-1 mr-md-6 mr-0 mb-md-0 mb-6 tab-group"
							@tab-selected="switchTab($event)"
							ref="tabGroup"
							v-if="!loading && hasMtFiles"
						>
							<template v-slot:tab_people>
								<div class="reservation-label text-left mb-2" v-if="mtFiles && mtFiles.length">
									{{$t('reservation.select_person_assigned_to_account_label')}}
								</div>
								<div>
									<Select 
										v-if="mtFiles.length"
										v-model="selectedMtFile"
										:options="mtFiles"
										:key="mtFilesSelectKey"
										:placeholder="$t('select_from_list_placeholder')"
										:label="$vuetify.display.mobile ? $t('select_from_list_label') : ''"
										@change="loadMtFileData($event)"
										class="select-personal-data">
										<template #option="{ option }">
											<div class="text-left" style="white-space: break-spaces">{{ option.name }}</div>
										</template>
									</Select>
								</div>
								

							</template>
							<template v-slot:tab_other></template>
						</TabGroup>
						<div
							v-if="!loading && (!(hasMtFiles && !selectedMtFile) || tabSelected == 'tab_other' || hasLastData)"
							class="mt-0 mb-0 mb-md-6 reservation-screen-wrapper d-flex flex-column pa-6 pt-0 align-start"
						>	
							<div class="validation mt-1 text-left" v-if="!validationTable['person_file_data']">
								{{$t('user.personal_data.person_file_data')}}
							</div>
							<div class="validation mt-1 text-left" v-if="!validationTable['mt_request_error']">
								{{$t('user.personal_data.mt_request_error')}}
							</div>
							<div class="validation mt-1 text-left" v-if="!validationTable['patient_mt_person_create_error']">
								{{$t('user.personal_data.patient_mt_person_create_error')}}
							</div>
							<div class="validation mt-1 text-left" v-if="!validationTable['person_file_create']">
								{{$t('user.personal_data.person_file_create')}}
							</div>
							<div class="validation mt-1 text-left" v-if="!validationTable['person_data_dictionary_error']">
								{{$t('user.personal_data.person_data_dictionary_error')}}
							</div>

							<div class="reservation-screen-subheader d-flex flex-row align-center text-left mt-10">
								<div class="reservation-label">
									{{$t('reservation.personal_data_subheader')}}
								</div>
							</div>
							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.name_label')}}</div>
								<input
									type="text"
									class="input"
									id="name"
									v-model="userData.name"
									@change="validateFields"
								/>
								<div class="validation mt-1 text-left" v-if="!validationTable['name']">
									{{$t('user.personal_data.name_validation_empty_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.lastname_label')}}</div>
								<input
									type="text"
									class="input"
									id="lastname"
									v-model="userData.lastname"
									@change="validateFields"
								/>
								<div class="validation mt-1 text-left" v-if="!validationTable['lastname']">
									{{$t('user.personal_data.lastname_validation_empty_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.phone_label')}}</div>
								<input
									type="text"
									class="input"
									id="phone"
									v-model="userData.phone"
									maxlength="17"
									@change="validateFields"
									@keypress="onlyPhoneKey($event)"
								/>
								<div class="validation mt-1 text-left" v-if="!validationTable['phone']">
									{{$t('user.personal_data.phone_validation_empty_msg')}}
								</div>
								<div class="validation mt-1 text-left" v-if="badPhoneNumber">
									{{$t('user.personal_data.phone_validation_invalid_msg')}}
								</div>
							
							</div>
							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.birth_date_label')}}</div>
								<div class="full-date-wrapper text-left d-flex flex-row">
									<span class="date-wrapper mr-2 flex-md-grow-0 flex-grow-1">
										<select
											:class="[$vuetify.display.mobile ? 'long select pr-8' : 'select pr-8']"
											v-model="userData.birthdate.day"
											@change="validateFields"
											id="birthdate_day"
										>
											<option value="" selected disabled>{{$t('user.personal_data.birth_date_day_label')}}</option>
											<option v-for="index in 31" :key="index" :value="index.toString().padStart(2,'0')">{{index}}</option>
										</select>
										<div class="chevron">
											<img :src="chevron_icon"/>
										</div>
									</span>
									<span class="date-wrapper mr-2 flex-md-grow-0 flex-grow-1">
										<select
											:class="$vuetify.display.mobile ? 'long select pr-8' : 'select pr-8'"
											v-model="userData.birthdate.month"
											@change="validateFields"
											id="birthdate_month"
										>
											<option value="" selected disabled>{{$t('user.personal_data.birth_date_month_label')}}</option>
											<option value="01">{{$t('months.january')}}</option>
											<option value="02">{{$t('months.february')}}</option>
											<option value="03">{{$t('months.march')}}</option>
											<option value="04">{{$t('months.april')}}</option>
											<option value="05">{{$t('months.may')}}</option>
											<option value="06">{{$t('months.june')}}</option>
											<option value="07">{{$t('months.july')}}</option>
											<option value="08">{{$t('months.august')}}</option>
											<option value="09">{{$t('months.september')}}</option>
											<option value="10">{{$t('months.october')}}</option>
											<option value="11">{{$t('months.november')}}</option>
											<option value="12">{{$t('months.december')}}</option>
										</select>
										<div class="chevron">
											<img :src="chevron_icon"/>
										</div>
									</span>
									<span class="date-wrapper flex-md-grow-0 flex-grow-1">
										<select
											:class="$vuetify.display.mobile ? 'long select pr-8' : 'select pr-8'"
											v-model="userData.birthdate.year"
											@change="validateFields"
											id="birthdate_year"
										>
											<option value="" selected disabled>{{$t('user.personal_data.birth_date_year_label')}}</option>
											<option v-for="index in 100" :key="index" :value="(new Date()).getFullYear()-index">{{(new Date()).getFullYear()-index}}</option>
										</select>
										<div class="chevron">
											<img :src="chevron_icon"/>
										</div>
									</span>
								</div>
								<div class="validation mt-1 text-left" v-if="!validationTable['birthdate']">
									{{$t('user.personal_data.birth_date_validation_invalid_msg')}}
								</div>
								<div class="validation mt-1 text-left" v-if="!validationTable['birthdate_adult']">
									{{$t('user.personal_data.birth_date_validation_invalid_adult_msg')}}
								</div>
								<div class="validation mt-1 mb-1 text-left" v-if="!validationTable['pesel_birthdate']">
									{{$t('user.personal_data.pesel_validation_invalid_birth_date_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.citizenship_label')}}</div>
								<div class="select-wrapper">
									<select
										class="select"
										v-model="userData.citizenship"
										@change="validateFields"
										id="citizenship"
									>
										<option value="" selected disabled>{{$t('select_from_list_label')}}</option>
										<option v-for="(citizenship, index) in dicts.citizenship" :value="citizenship.code" :key="index">{{citizenship.value}}</option>
									</select>
									<div class="chevron">
										<img :src="chevron_icon"/>
									</div>
								</div>
								<div class="validation mt-1 text-left" v-if="!validationTable['citizenship']">
									{{$t('user.personal_data.citizenship_validation_empty_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.gender_label')}}</div>
								<div class="select-wrapper">
									<select
										class="select"
										v-model="userData.gender"
										@change="validateFields"
										id="gender"
									>
										<option value="" selected disabled>{{$t('select_from_list_label')}}</option>
										<option v-for="(gender, index) in genders" :value="gender.code" :key="index">{{gender.name}}</option>
									</select>
									<div class="chevron">
										<img :src="chevron_icon"/>
									</div>
								</div>
								<div class="validation mt-1 text-left" v-if="!validationTable['gender']">
									{{$t('user.personal_data.gender_validation_empty_msg')}}
								</div>
								<div class="validation mt-1 mb-1 text-left" v-if="!validationTable['pesel_gender']">
									{{$t('user.personal_data.pesel_validation_invalid_gender_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8 text-left">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.pesel_number_label')}}</div>
								<input
									type="text"
									class="input"
									v-model="userData.pesel"
									@change="validateFields"
									@keypress="onlyNumberKey($event)"
									@paste="onlyNumberPaste($event)"
									maxLength="11"
									:disabled="userData.noPesel"
									id="pesel"
								/>
								<div class="validation mt-1 mb-1 text-left" v-if="!validationTable['pesel']">
									{{$t('user.personal_data.pesel_validation_invalid_msg')}}
								</div>
								<div class="validation mt-1 mb-1 text-left" v-if="!validationTable['pesel_birthdate']">
									{{$t('user.personal_data.pesel_validation_invalid_birth_date_msg')}}
								</div>
								<div class="validation mt-1 mb-1 text-left" v-if="!validationTable['pesel_gender']">
									{{$t('user.personal_data.pesel_validation_invalid_gender_msg')}}
								</div>
								<label class="container input-label mt-2" id="noPesel-label">{{$t('user.personal_data.no_pesel_label')}}
									<input
										type="checkbox"
										v-model="userData.noPesel"
										@change="validateFields"
										id="noPesel"
									>
									<span class="checkmark"></span>
								</label>
							</div>
							<div class="input-wrapper mt-8 text-left" v-if="userData.noPesel === true">
								<div class="input-label text-left mb-1 ml-6">{{$t('user.personal_data.document_type_label')}}</div>
								<div class="select-wrapper ml-6 mr-6">
									<select
										class="select"
										v-model="userData.documentType"
										id="documentType"
										@change="validateFields"
									>
										<option value="" selected disabled>{{$t('select_from_list_label')}}</option>
										<option v-for="(documentType, index) in tabSelected == 'tab_other' || (!mtFiles.length) ? dicts.doc_id_filtered : dicts.doc_id" :value="documentType.code" :key="index">{{$t(`doc_id.${documentType.code}`)}}</option>
									</select>
									<div class="chevron">
										<img :src="chevron_icon"/>
									</div>
								</div>
								<div class="validation mt-1 ml-6 mr-6 text-left" v-if="!validationTable['documentType']">
									{{$t('user.personal_data.document_type_validation_empty_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8" v-if="userData.noPesel === true">
								<div class="input-label text-left mb-1 ml-6">{{$t('user.personal_data.document_number_label')}}</div>
								<div class="ml-6 mr-6">
									<input
										type="text"
										class="input"
										v-model="userData.documentNumber"
										id="documentNumber"
										@change="validateFields"
									/>
								</div>
								<div class="validation mt-1 ml-6 mr-6 text-left" v-if="!validationTable['documentNumber']">
									{{$t('user.personal_data.document_number_validation_empty_msg')}}
								</div>
							</div>
							<div class="input-wrapper mt-8 text-left" v-if="userData.noPesel === true">
								<div class="input-label text-left mb-1 ml-6">{{$t('user.personal_data.document_country_label')}}</div>
								<div class="select-wrapper ml-6 mr-6">
									<select
										class="select"
										v-model="userData.documentCountry"
										id="documentCountry"
										@change="validateFields"
									>
										<option value="" selected disabled>{{$t('select_from_list_label')}}</option>
										<option v-for="(documentCountry, index) in dicts.country" :value="documentCountry.code" :key="index">{{documentCountry.value}}</option>
									</select>
									<div class="chevron">
										<img :src="chevron_icon"/>
									</div>
								</div>
								<div class="validation mt-1 ml-6 mr-6 text-left" v-if="!validationTable['documentCountry']">
									{{$t('user.personal_data.document_country_validation_empty_msg')}}
								</div>
							</div>

							<!---------- ADDRESS ----------->
							<div v-if="!loading_address && userAddress" class="input-wrapper">
								<div class="input-wrapper mt-8">
									<div class="input-label text-left mb-1">{{$t('user.address.country_label')}}</div>
									<div class="select-wrapper">
										<select
											class="select"
											id="country_id"
											v-model="userAddress.country_id"
											@change="changeCountry"
										>
											<option value="" selected disabled>{{$t('select_from_list_label')}}</option>
											<option v-for="(country, index) in dicts.country" :value="country.id" :key="index">{{country.value}}</option>
										</select>
										<div class="chevron">
											<img :src="chevron_icon"/>
										</div>
									</div>
									<div class="validation mt-1 text-left" v-if="!validationTable['country_id']">
										{{$t('user.address.country_validation_empty_msg')}}
									</div>
								</div>
								<div class="input-wrapper mt-8" v-if="userAddress.country_id == 1 && !loading_address">
									<div class="input-label text-left mb-1">{{$t('user.address.voivodeship_label')}}</div>
									<div class="select-wrapper">
										<select
											class="select"
											id="voivodeship_id"
											v-model="userAddress.voivodeship_id"
											@change="getLocalities"
										>
											<option value="" selected disabled>{{$t('select_from_list_label')}}</option>
											<option v-for="(voivodeship, index) in dicts.voivodeship" :value="voivodeship.code" :key="index">{{voivodeship.name}}</option>
										</select>
										<div class="chevron">
											<img :src="chevron_icon"/>
										</div>
									</div>
									<div class="validation mt-1 text-left" v-if="!validationTable['voivodeship_id']">
										{{$t('user.address.voivodeship_validation_empty_msg')}}
									</div>
								</div>
								<div class="input-wrapper mt-8" v-if="!loading_localities && (userAddress.voivodeship_id || userAddress.country_id != 1)">
									<div class="input-label text-left mb-1">{{$t('user.address.locality_label')}}</div>
									<div class="d-flex flex-md-row flex-column">
										<input
											type="text"
											class="input"
											id="locality"
											v-model="userAddress.locality"
											@change="validateFields"
											@keyup.enter="filterLocalities"
										/>
										<button
											@click="filterLocalities"
											v-if="userAddress.voivodeship_id && userAddress.country_id == 1"
										>
											{{$t('user.address.locality_search_btn')}}
										</button>
									</div>
									<div class="validation mt-1 text-left" v-if="!validationTable['locality']">
										{{$t('user.address.locality_validation_empty_msg')}}
									</div>
								</div>
								<div
									class="table-wrapper mt-8"
									v-if="userAddress?.voivodeship_id && !this.loading_localities && userAddress.country_id == 1 && !loading_address"
								>
									<table-component
										ref="table"
										:headers="localityHeaders"
										:data="filteredLocalityData"
										:start-data-selected="localityDataSelected"
										class="table"
										@selected="fillForm"
									/>
								</div>
								<div v-else class="d-flex flex-row justify-center align-center w-100">
									<v-progress-circular
										indeterminate
										color="primary"
										size="22"
										width="2"
										class="my-6"
										v-if="userAddress?.voivodeship_id"
									></v-progress-circular>
								</div>
								
								<div class="input-wrapper mt-8" v-if="userAddress.country_id == 1 && !loading_address">
									<div class="input-label text-left mb-1">{{$t('user.address.commune_label')}}</div>
									<input
										readonly
										type="text"
										class="input"
										id="commune"
										v-model="userAddress.commune"
									/>
								</div>

								<div class="input-wrapper mt-8" v-if="userAddress.country_id == 1 && !loading_address">
									<div class="input-label text-left mb-1">{{$t('user.address.poviat_label')}}</div>
									<input
										readonly
										type="text"
										class="input text-capitalize"
										id="poviat"
										v-model="userAddress.poviat"
									/>
								</div>
								
								<div class="input-wrapper mt-8">
									<div class="input-label text-left mb-1">{{$t('user.address.zip_code_label')}}</div>
									<input
										type="text"
										class="input"
										id="zip_code"
										v-model="userAddress.zip_code"
										:maxlength="userAddress.country_id == 1 ? 6 : 12"
										@change="formatPostalCode()"
										@paste="formatPostalCode()"
										@keyup="validateFields"
									/>
									<div class="validation mt-1 text-left" v-if="!validationTable['zip_code']">
										{{$t('user.address.zip_code_validation_invalid_msg')}}
									</div>
								</div>
								
								<div class="input-wrapper mt-8" v-if="userAddress.country_id == 1">
									<div class="input-label text-left mb-1">{{$t('user.address.post_office_label')}}</div>
									<input
										type="text"
										class="input"
										id="post_office"
										@change="validateFields"
										v-model="userAddress.post_office"
									/>
									<div class="validation mt-1 text-left" v-if="!validationTable['post_office']">
										{{$t('user.address.post_office_validation_invalid_msg')}}
									</div>
								</div>

								<div class="input-wrapper mt-8">
									<div class="input-label text-left mb-1">{{$t('user.address.street_label')}}</div>
									<input
										type="text"
										class="input"
										id="street"
										@change="validateFields"
										v-model="userAddress.street"
									/>
									<div class="validation mt-1 text-left" v-if="!validationTable['street']">
										{{$t('user.address.street_validation_invalid_msg')}}
									</div>
								</div>

								<div class="input-wrapper mt-8">
									<div class="input-label text-left mb-1">{{$t('user.address.house_number_label')}}</div>
									<input
										type="text"
										class="input"
										id="house_number"
										@change="validateFields"
										v-model="userAddress.house_number"
									/>
									<div class="validation mt-1 text-left" v-if="!validationTable['house_number']">
										{{$t('user.address.house_number_validation_invalid_msg')}}
									</div>
								</div>

								<div class="input-wrapper mt-8">
									<div class="input-label text-left mb-1">{{$t('user.address.apartment_number_label')}}</div>
									<input
										type="text"
										class="input"
										id="apartment_number"
										v-model="userAddress.apartment_number"
									/>
								</div>
							</div>
							<div v-else>
								<v-progress-circular
									indeterminate
									color="primary"
									size="22"
									width="2"
									class="my-6"
								></v-progress-circular>
							</div>
							<!------------------------------>

							<div class="section-header mt-12">{{$t('user.personal_data.location_choice_header')}}</div>

							<div class="input-wrapper mt-8">
								<div class="input-label text-left mb-1">{{$t('user.personal_data.location_label')}}</div>
								<div class="select-wrapper">
									<Select
										v-if="transportPrices && transportPrices.length"
										v-model="userData.location"
										id="location"
										:clearIcon="false"
										:options="mzTreatmentLocation ? [] : transportPrices"
										:placeholder="$t('select_from_list_label')"
										:sortData="false"
										@change="changeLocation"
										:class="[mzTreatmentLocation ? 'disabled' : '', 'select-location-data']">
										<template #input="{ modelValue }">
											<div class="text-left d-flex flex-row justify-space-between w-100 select-mock" style="white-space: break-spaces">
												<div>{{ modelValue?.name ? $t('INVICTA-CLINIC') + ' ' + modelValue.name : $t('select_from_list_label') }}</div>
												<div v-if="modelValue.name && modelValue.transport.length" class="transport-price mr-6">
													+ {{$n(modelValue.transport.reduce((accumulator, currentValue) => accumulator + parseInt(currentValue.price), 0) | 0, 'currency', 'pl')}}
												</div>
											</div>
										</template>
										<template #option-top>
											<div v-if="!mzTreatmentLocation" class="option-top-label pa-4 pa-md-3 d-flex flex-row justify-space-between w-100">
												<span>{{$t('user.personal_data.location_label')}}</span>
												<span class="mr-2 mr-md-0">{{$t('transport.price_label')}}</span>
											</div>
										</template>
										<template #option="{ option }">
											<div class="text-left d-flex flex-column w-100">
												<div
													class="text-left d-flex flex-row justify-space-between w-100"
													style="white-space: break-spaces"
													:class="{'option-disabled': option.id == -1}"
												>
													<div>{{$t('INVICTA-CLINIC')}} {{ option.name }}</div>
													<div class="transport-price mr-2 mr-md-0" v-if="option.transport.length">
														+ {{$n(option.transport.reduce((accumulator, currentValue) => accumulator + parseInt(currentValue.price), 0) | 0, 'currency', 'pl')}}
													</div>
												</div>
												<div class="transport-time-info d-flex flex-wrap">
													{{ $t('transport.estimated_time_label') }}: 
													<span class="mx-1">
														{{ option.transport.length ? 
															$t('transport.n_weeks_waiting_info') : 
															$t('transport.material_at_clinic_info') }}
													</span>
												</div>
											</div>
										</template>
									</Select>
								</div>
								<div class="validation mt-1 text-left" v-if="!validationTable['location']">
									{{$t('user.personal_data.location_validation_empty_msg')}}
								</div>
								<!-- <div class="information mt-3 text-left" v-if="!validationTable['different_location']">
									{{$t('reservation.different_location_info')}}
								</div> -->
							</div>
							<div class="consent-wrapper text-left mt-16" v-if="!loading_consents">
								<div class="consent mb-3t" v-if="isTransportNeeded">
									<label class="container input-label mt-2 d-flex flex-row align-start">
										<span> {{$t('reservation.transport_consent_content')}}
											<a class='text-decoration-underline' @click="showTransportConditionsDialog">
												{{$t('reservation.transport_conditions_text_link')}}
											</a>.*
										</span>
										<input type="checkbox"
											v-model="transportConsent"
											@change="changeTransportConsent">
										<span class="checkmark"></span>
									</label>
									
									<div class="validation mt-1 ml-6 text-left" v-if="!validationTable['transport_consent']">
										{{$t('reservation.consents_validation_empty_msg')}}
									</div>
								</div>
								<div class="consent mb-3t" v-for="(consent, index) in allConsents" :key="index">
									<label class="container input-label mt-2 d-flex flex-row align-start">
										<span v-html="parseConsent(consent)"></span>
										<input type="checkbox"
											v-model="recipientConsents[consent.code].consent_value">
										<span class="checkmark"></span>
									</label>
									
									<div class="validation mt-1 ml-6 text-left" v-if="!validateConsent(consent.code)">
										{{$t('reservation.consents_validation_empty_msg')}}
									</div>
								</div>
							</div>
						</div>

					</div>
					<div class="order-summary">
						<order-summary-component
							:cart_size="size"
							:value="value"
							:discount-items="discountItems"
							:transport="transportPrices"
							:location="userData.location?.code"
							:booking-callback="saveReservation"
							:loading="loading"
						></order-summary-component>
					</div>
				</div>
				<div v-else>
					<v-progress-circular
						indeterminate
						color="primary"
						size="22"
						width="2"
						class="my-6"
					></v-progress-circular>
				</div>
			</template>

			<template v-slot:slot_2>
				
			</template>

			<template v-slot:slot_3>
				<div class="thank-you-screen d-flex flex-column pa-6">
					<div v-if="screen_ready">
						<div class="info-wrapper pa-6">
							<div class="info-header-wrapper text-left">
								<img
									class="div-1 info-icon ma-md-3"
									:src="success ? success_icon : failure_icon"
									width="38"
									height="38"
								/>
								<div class="div-2 reservation-screen-header text-left">
									{{ success ? $t('reservation.screen_success_header') : $t('reservation.screen_failure_header')}}
								</div>

								<div v-if="reason" class="div-3 reservation-screen-text reason mt-6 text-left">
									{{ reason }}
								</div>
								
								<div v-if="getPayment && success" class="div-4 reservation-screen-text mt-3 text-left">
									{{$t('reservation.screen_success_with_payment_text')}}
								</div>

								<div v-if="success" class="div-5 reservation-screen-text mt-3 text-left">
									{{ $t('reservation.screen_success_text') }}
									<br/>
									<v-btn
										v-if="!getPayment"
										flat
										rounded
										color="primary"
										height="48"
										class="text-uppercase mt-4 mb-6"
										:disabled="cart_size < 1 || loading"
										@click="redirect()"
									>
										{{ $t('reservation.screen_success_text_link') }}
									</v-btn>
								</div>
								<div v-else class="div-5 reservation-screen-text mt-6 text-left">
									{{$t('reservation.screen_failure_text')}}
								</div>
							</div>
						</div>

					</div>
					<v-progress-circular
						v-else
						indeterminate
						color="primary"
						class="progress_summary"
					></v-progress-circular>
				</div>
			</template>
		</chained-dialog-component>
	</v-main>
</template>
  
<script>
import { useStore, mapGetters } from 'vuex'
import { computed, ref } from 'vue'
import ChainedDialogComponent from '../components/ChainedDialogComponent.vue';
import OrderSummaryComponent from '@/components/OrderSummaryComponent.vue';
import TableComponent from '@/components/TableComponent.vue';
import { TabGroup, Select } from "design-system-ui-components";
import reservation from "@/utils/reservation_util";
import TransportConditionsComponent from '@/components/TransportConditionsComponent.vue';
import LegalComponent from '@/components/LegalComponent.vue';

export default {
	components: {
		ChainedDialogComponent,
		OrderSummaryComponent,
		TableComponent,
		TabGroup,
		Select,
		TransportConditionsComponent,
		LegalComponent
	},
	data() {
		return {
			selectedMtFile: null,
			tabSelected: null,
			allConsents: [],
			recipientConsents: null,
			hasMtFiles:false,
			hasLastData: false,
			mtFile: null,
			mtFiles: null,
			currentSlot: 1,
			validationOn: false,
			validationTable: {
				name: true,
				lastname: true,
				phone: true,
				birthdate: true,
				birthdate_adult: true,
				pesel_birthdate: true,
				citizenship: true,
				gender: true,
				pesel_gender: true,
				pesel: true,
				documentType: true,
				documentNumber: true,
				documentCountry: true,
				country_id: true,
				voivodeship_id: true,
				zip_code: true,
				post_office: true,
				street: true,
				house_number: true,
				location: true,
				locality: true,
				different_location: true,
				transport_consent: true,
				person_file_data: true,
				mt_request_error: true,
				patient_mt_person_create_error: true,
				person_file_create: true,
				person_data_dictionary_error: true
			},
			loading: true,
			loading_consents: true,
			loading_address: false,
			value: 0,
			size: 0,
			cart: {
				items: {}
			},
			userAddress: null,
			loading_localities: false,
			localityData: [],
			filteredLocalityData: [],
			localityDataSelected: false,
			badPhoneNumber: false,
			loading_save: false,
			success: false,
			screen_ready: false,
			discountItems: [],
			transportPrices: [],
			transportConsent: false,
			userFileData: {
				name: '',
				lastname: '',
				phone: '',
				birthdate: {
					day: '',
					month: '',
					year: ''
				},
				gender: '',
				citizenship: '',
				pesel: '',
				noPesel: false,
				documentType: '',
				documentNumber: '',
				documentCountry: '',
				location: '',
				file_id: null,
			},
			userData: {
				name: '',
				lastname: '',
				phone: '',
				birthdate: {
					day: '',
					month: '',
					year: ''
				},
				gender: '',
				citizenship: '',
				pesel: '',
				noPesel: false,
				documentType: '',
				documentNumber: '',
				documentCountry: '',
				location: '',
			},
			localities: [],
			reason: null,
			transportConditionsDialogVisible: false,
			legalInformationDialogVisible: false
		}
	},
	setup() {
		const originUrl = window.location.origin
		const mtFilesSelectKey = ref(0)
		const store = useStore();
		
		const close_icon = computed({
			get: () => { return require('../assets/icons/chevron-left.svg'); }
		})

		const data_icon = computed({
			get: () => { return require('../assets/icons/box.svg'); }
		})

		const chevron_icon = computed({
			get: () => { return require('../assets/icons/chevron-down.svg'); }
		})
		const success_icon = computed(() => {
			return require('../assets/icons/payment_success.svg');
		})
		const failure_icon = computed(() => {
			return require('../assets/icons/payment_failure.svg');
		})

		return {
			success_icon,
			failure_icon,
			mtFilesSelectKey,
			close_icon,
			data_icon,
			chevron_icon,
			originUrl,
			oidcUser: computed(() => store.getters['oidcStore/oidcUser']),
		}
	},
	watch: {
		'userData.noPesel': {
			handler() {
				this.userData.pesel = ''
			}
		},
		'$route.query._locale': {
			handler(locale) {
				reservation.getDictionaries(locale).then(response => {
					this.dicts = response
				})
			}
		}
	},
	computed: {
		...mapGetters('reservationModule', ['getReservation','getChecked','getPayment']),
		...mapGetters('cartModule', ['getCount','getAcceptanceIdByItemId']),
		tabGroupSettings() {
			return [
				{
					label: this.$t('reservation.tab_people_account_name'),
					name: 'tab_people',
					index: 0
				},
				{
					label: this.$t('reservation.tab_other_person_name'),
					name: 'tab_other',
					index: 1
				},
			]
		},
		genders() {
			return [
				{
					name: this.$t('MALE'),
					code: "male",
				},
				{
					name: this.$t('FEMALE'),
					code: "female",
				}
			]
		},
		localityHeaders() {
			return [
				{
					name: this.$t('user.address.locality_label'),
					key: "locality",
					index: 0
				},
				{
					name: this.$t('user.address.commune_label'),
					key: "commune",
					index: 1
				},
				{
					name: this.$t('user.address.commune_type_label'),
					key: "commune_type",
					index: 2
				},
				{
					name: this.$t('user.address.poviat_label'),
					key: "poviat",
					index: 3
				},
			]
		},
		isTransportNeeded() {
			return this.userData.location.transport?.length ? true : false
		},
		mzTreatmentLocation() {
			return this.discountItems.length ? 
				this.transportPrices.find(e => e.name == this.discountItems[0].material_location_name) 
				: null
		}

	},
	created() {
		// STAGE 0 - get independent data
		reservation.getCart()
		.then(response => {
			let cart = response;
			Object.keys(cart.items).forEach(item => {
				cart.items[item].checked = !!this.getChecked.find(e => e == item);
			})
			this.value = this.getCartValue(cart);
			this.size  = this.getCartSize(cart);
			this.discountItems = this.getCartDiscountItems(cart);
			this.cart = cart;
		})
		.then(() => {
			reservation.getTransportPrices(
				this.$i18n.locale,
				Object.keys(this.cart.items).filter(e => this.cart.items[e].checked)
			)
			.then((response) => {
				this.transportPrices = response;
				if (this.mzTreatmentLocation) {
					this.userData.location = this.mzTreatmentLocation;
				}
			});
		});
	},
	mounted() {
		this.loading = true;
		this.loading_consents = true;
		this.loading_address = true;
		this.$store.dispatch('breadcrumbModule/setRoute', [{
			name: 'breadcrumb.want_to_be_a_parent',
			url: `https://bank.invicta.pl${ this.$t('bank_invicta_path.planning_parenthood') }`,
			absolute: true
		},{
            name: 'breadcrumb.reservation_labels.summary',
            url: '/cart'
        },
        {
            name: 'breadcrumb.reservation_labels.data',
            url: ''
        }
        ])
		// STAGE 1 - get basic user data and file_id
		Promise.all([
			reservation.getDictionaries(this.$i18n.locale),
			reservation.getUserFilesFromMT(),
			reservation.getLastUserData()
		])
		.then(response => {
			// reset variables
			this.fileId = null;
			this.selectedMtFile = null;

			this.dicts = response[0];

			this.mtFiles = response[1];
			this.lastUserData = response[2];

			// set switches
			this.hasLastData = !!this.lastUserData && this.lastUserData.record_data.file_id;
			this.hasDiscountItems = !!this.discountItems.length;
			this.hasMtFiles = !!this.mtFiles && this.mtFiles.length > 0

			if(this.hasDiscountItems) {
				this.fileId = this.discountItems[0].mz_grant_file_id
			} else if(this.hasLastData && this.lastUserData.record_data.file_id){
				this.fileId = this.lastUserData.record_data.file_id;
			}

			// extend dictionaries to account for strange document id
			// policy in mt
			this.dicts.doc_id_filtered = this.dicts.doc_id.filter(e => 
				e.code == 'ID_CARD'  ||
				e.code == 'PASSPORT' ||
				e.code == 'DRIVING_LICENSE'
			)

			// at this point we have an idea of file_id and
			// user data existence so we can react accordingly
		})
		// STAGE 2 - get all consents
		.then(() => {
			Promise.all([
				reservation.getConsentsByScope("reservation"),
				reservation.getRecipientConsentsByScope("reservation", this.hasLastData || this.hasDiscountItems ? this.fileId : null)
			])
			.then(result => {
				this.allConsents = result[0];
				this.recipientConsents = result[1];
			})
			.finally(() => {
				this.loading_consents = false;
				setTimeout(() => { this.onConsentLoad() }, 100);
			})
			
		})
		// STAGE 3 - set address if available
		.then(() => {
			this.userAddress = reservation.getEmptyUserAddress();
			if(this.hasLastData || this.hasDiscountItems) {
				this.getUserAddressByFileId(this.fileId);
			} else {
				this.loading_address = false;
			}
		})
		// STAGE 4 - set last data/grant file data if available
		.then(() => {
			if(this.hasDiscountItems) {
				this.setUserData(this.mtFiles.find(e => e.file_id == this.fileId));
				this.loading = false;
			} else
			if(this.hasLastData) {
				this.setUserData(this.lastUserData.record_data);
				this.loading = false;
			}
		})
		// STAGE 5 - set proper tab, populate mt files
		//			 select if needed
		.then(() => {
			// prepare mtFiles to work as options
			this.mtFiles = this.mtFiles
			.map(e => {
				return {
					...e,
					identity_doc_country: 
						e.identity_doc_country && e.identity_doc_country?.length == 2
							? this.dicts.country.find(f => f.code == e.identity_doc_country).value
							: e.identity_doc_country || null,
					value: e.file_id,
					name: `${e.first_name} ${e.last_name} - ${ e.is_pesel ?
						this.$t('user.personal_data.pesel_number_label')+': ' + e?.pesel_number :
						this.$t('user.personal_data.birth_date_short_label')+' ' + e?.birth_date}`
				}
			})
			.filter(
				
			e => {
				let date = new Date(e.birth_date);
				let now  = new Date();
				now.setFullYear(now.getFullYear() - 18);
				return date.getTime() < now.getTime();
			});
			// see if we have discunted material in the cart
			// if yes, match patient file that has proper grant
			// for this material
			if(this.discountItems.length) {
				this.selectedMtFile = this.mtFiles.find(e => e.file_id == this.discountItems[0].mz_grant_file_id)
			} else
			// if no, set last used mtFile as current
			// in patient file select
			if(this.hasLastData) {
				this.selectedMtFile = this.mtFiles.find(e => e.file_id == this.lastUserData.record_data.file_id)
			}
		})
		.catch((error) => {
			console.error(error);
		})
		.finally(() => {
			this.loading = false;
		});
	},
	methods: {
		loadMtFileData(mtFile) {
			this.transportConsent = false;
			// after selecting mtFile we can load more things
			if(mtFile) {
				// recalculate cart
				this.recalculateCart(mtFile);
				// data
				this.setUserData(mtFile);
				this.fileId = mtFile.file_id;
				// address
				this.getUserAddressByFileId(mtFile.file_id);
				// consents
				reservation.getRecipientConsentsByScope("reservation", mtFile.file_id)
				.then(result => {
					this.recipientConsents = result;
				})
				.finally(() => {
					this.loading_consents = false;
					// this.validateFields()
				})
			} else {
				this.resetUserData();
				this.userAddress = reservation.getEmptyUserAddress();
				this.selectedMtFile = false;
				this.mtFile = null;
			}
		},

		getUserAddressByFileId(fileId) {
			reservation.getUserAddressByFileId(fileId)
			.then(result => {
				if(result.commune && result.commune.split('(').length > 1) {
					// we need this contraption to distinguish
					// between very similar looking communes
					this.loading_address = true;
					let commune_name = result.commune.split('(')[0];
					let commune_type = result.commune.split('(')[1].split(',')[1];
					commune_type = commune_type.substr(1, commune_type.length - 2);
					reservation.getLocalities(result.voivodeship_id)
					.then((localities) => {
						// set address
						this.userAddress = result;
						// determine proper commune
						let commune = localities.find(e => 
							e.commune_type == commune_type &&
							e.commune == commune_name &&
							e.locality == result.locality
						)
						if(!commune) {
							commune = localities.find(e => 
								e.commune == commune_name
							)
						}
						// if poviat came as number, change it
						// to proper name
						if(!isNaN(result.poviat)){
							let poviat = localities.find(e => e.poviat_id == result.poviat).poviat;
							this.userAddress.poviat = poviat;
						}
						// set localities table
						this.localityData = localities;
						this.filteredLocalityData = localities;
						// set rest of address
						this.userAddress.commune = commune.commune;
					})
					.finally(() => {
						this.loading_address = false;
						// filter localities
						if (this.userAddress.locality) {
							this.filterLocalities()
						}
					})
				} else {
					this.loading_address = true;
					this.userAddress = result;
					reservation.getLocalities(result.voivodeship_id)
					.then((localities) => {
						if (this.userAddress.locality) {

							let fl = localities.filter(e => 
								e.locality == result.locality
							)
							if (fl.length == 1) {
								this.userAddress.commune = fl[0].commune;
								this.userAddress.poviat = fl[0].poviat;
							}

							// set localities table
							this.localityData = localities;
							this.filteredLocalityData = localities;
						}
					}).finally(() => {
						this.loading_address = false;
						// filter localities
						if (this.userAddress.locality) {
							this.filterLocalities()
						}
					})
				}
			})
		},

		recalculateCart(file) {
			this.discountItems = []
			Object.keys(this.cart.items)
				.forEach(id => {
					let mzGrantFileId = null
					if (file) {
						if (
							this.cart.items[id].material_type == "mtSemen" && 
							(this.cart.items[id].want_mz_grant && file.has_mz_semen)
						) {
							this.discountItems.push(this.cart.items[id])
							mzGrantFileId = file.file_id
						}
						if (
							this.cart.items[id].material_type == "mtCell" && 
							(this.cart.items[id].want_mz_grant && file.has_mz_cell)
						) {
							this.discountItems.push(this.cart.items[id])
							mzGrantFileId = file.file_id
						}
					}

					reservation.updateCartItem(
						this.cart.items[id],
						mzGrantFileId,
						this.userData.location?.id,
						this.transportConsent
					).then(() => {
						this.$store.dispatch('cartModule/setItemMzGrantFileId', {
							id: id,
							mzGrantFileId: mzGrantFileId
						})
					})
				})
		},

		setUserData(data) {
			this.resetUserData();
			this.validationOn = true;

			this.userData.birthdate = {
				day: data.birth_date?.split('-')[2],
				month: data.birth_date?.split('-')[1],
				year: data.birth_date?.split('-')[0]
			};

			this.userData.citizenship = data.citizenship ? data.citizenship : '';
			if (this.mzTreatmentLocation) {
				this.userData.location = this.mzTreatmentLocation;
			}
			// this.userData.location = data.location_of_treatment ? data.location_of_treatment : '';
			
			if(data.identity_doc_country?.length <= 2) {
				this.userData.documentCountry = !data.is_pesel ? data.identity_doc_country : "";
			} else {
				this.userData.documentCountry = !data.is_pesel ? this.dicts.country.find(f => f.name == data.identity_doc_country).code : "";
			}
			this.userData.documentNumber = !data.is_pesel ? data.identity_doc_number : "";
			this.userData.documentType = !data.is_pesel ? data.identity_doc_type : "";

			this.userData.name = data.first_name;
			this.userData.lastname = data.last_name;
			this.userData.phone = data.phone_number;
			this.userData.gender = data.gender;

			this.userData.noPesel = !data.is_pesel;

			this.userData.file_id = data.file_id;
			this.userData.pesel = data.is_pesel ? data.pesel_number : '';

			this.$nextTick(() => {
				// disabling fields can only happen if they are present
				// in the DOM, hence we use next tick here
				document.querySelector(`#documentCountry`)?.classList.add('disabled');
				document.querySelector(`#documentNumber`)?.classList.add('disabled');
				document.querySelector(`#documentType`)?.classList.add('disabled');
				document.querySelector(`#name`).classList.add('disabled');
				document.querySelector(`#lastname`).classList.add('disabled');
				document.querySelector(`#gender`).classList.add('disabled');
				if(this.userData.phone) {
					document.querySelector(`#phone`).classList.add('disabled');
				}
				document.querySelector(`#pesel`).classList.add('disabled');

				document.querySelector('#birthdate_day').classList.add('disabled');
				document.querySelector('#birthdate_month').classList.add('disabled');
				document.querySelector('#birthdate_year').classList.add('disabled');

				document.querySelector(`#noPesel`).disabled = true;
				document.querySelector(`#noPesel-label`).classList.add('disabled');
				if(this.userData.citizenship) {
					document.querySelector(`#citizenship`).classList.add('disabled');
				}
				// if(this.userData.location) {
				// 	document.querySelector(`#location`).classList.add('disabled');
				// }
			});
		},

		changeCountry() {
			this.validateFields()
			if(this.userAddress.country_id != 1) {
				this.userAddress.voivodeship_id = 0;
				this.userAddress.commune = '';
				this.userAddress.commune_id = 0;
				this.userAddress.poviat = '';
				this.userAddress.poviat_id = 0;
			}
		},

		changeLocation(e) {
			this.validationTable.location = true
			this.transportConsent = false
			Object.keys(this.cart.items)
				.forEach(id => {
					reservation.updateCartItem(
						this.cart.items[id],
						this.cart.items[id].mz_grant_file_id,
						e.id,
						this.transportConsent
					)
				})
		},

		changeTransportConsent() {
			this.validateFields()
			Object.keys(this.cart.items)
				.forEach(id => {
					reservation.updateCartItem(
						this.cart.items[id],
						this.cart.items[id].mz_grant_file_id,
						this.userData.location.id,
						this.transportConsent
					)
				})
		},

		fillForm(e) {
			this.userAddress.locality = e.locality;
			this.userAddress.commune = e.commune;
			this.userAddress.commune_id = e.commune_id;
			this.userAddress.poviat = e.poviat;
			this.userAddress.poviat_id = e.poviat_id;
		},

		filterLocalities() {
			// let filter = document.querySelector('#locality').value;
			let filter = this.userAddress.locality
			this.loading_localities = true;
			if (this.$refs.table) {
				this.$refs.table.selected_index = null;
			}
			this.filteredLocalityData = this.localityData.filter(e => e.locality.toLowerCase().startsWith(filter.toLowerCase()));

			if (this.filteredLocalityData.length == 1) {
				if (this.filteredLocalityData[0].commune == this.userAddress.commune && this.filteredLocalityData[0].poviat == this.userAddress.poviat) {
					this.localityDataSelected = true;
				}
			}

			this.loading_localities = false;
		},

		formatPostalCode() {
			let code = document.querySelector('#zip_code').value;
			if(!code.match(/^[0-9][0-9]-[0-9][0-9][0-9]$/) && this.userAddress.country_id == 1) {
				return false;
			} else {
				return true;
			}
		},

		getLocalities() {
			this.validateFields()
			return new Promise((resolve, reject) => {
				this.loading_localities = true;
				this.userAddress.poviat = '';
				this.userAddress.commune = '';
				this.userAddress.locality = '';
				reservation.getLocalities(this.userAddress?.voivodeship_id)
				.then(result => {
					this.localityData = result;
					this.filteredLocalityData = result;
					resolve();
				})
				.catch(error => {
					console.error(error);
					reject();
				})
				.finally(() => {
					this.loading_localities = false;
				})
			})
			
		},

		resetUserData() {
			this.validationOn = false;
			this.userData = reservation.getEmptyUserData();

			Object.keys(this.userData).forEach(key => {
				document.querySelector(`#${key}`)?.classList.remove('disabled');
			})
			if(document.querySelector(`#noPesel`)) {
				document.querySelector(`#noPesel`).disabled = false;
			}

			document.querySelector(`#noPesel-label`)?.classList.remove('disabled');
			document.querySelector('#birthdate_day')?.classList.remove('disabled');
			document.querySelector('#birthdate_month')?.classList.remove('disabled');
			document.querySelector('#birthdate_year')?.classList.remove('disabled');
		},

		switchTab(e) {
			this.transportConsent = false;
			this.tabSelected = e.name;
			this.$nextTick(() => {
				if(e.name != 'tab_other' && this.selectedMtFile) {
					this.setUserData(this.selectedMtFile);
					this.recalculateCart(this.selectedMtFile);
				} else {
					this.resetUserData();
					this.userAddress = reservation.getEmptyUserAddress();
					this.recalculateCart(null);
				}
			})
		},

		getCartValue(cart) {
			let full_price = 0;
			if(cart.items) {
				Object.keys(cart.items).forEach(e => {
					if(cart.items[e].checked) {
						full_price += cart.items[e].price * cart.items[e].quantity;
					}
				})
			}
			return full_price;
		},

		getCartSize(cart) {
			let cart_size = 0;
			if(cart.items) {
				Object.keys(cart.items).forEach(e => {
					if(cart.items[e].checked) {
						cart_size+= cart.items[e].quantity;
					}
				})
			}
			return cart_size;
		},

		getCartDiscountItems(cart) {
			let discountItems = [];
			if(cart.items) {
				Object.keys(cart.items).forEach(e => {
					if(cart.items[e].mz_grant_file_id && cart.items[e].checked) {
						discountItems.push(cart.items[e]);
					}
				})
			}
			return discountItems;
		},

		getProductName(item) {
			let name = '';
			let type = this.cart.items[item]?.material_type;
			switch(type) {
				case 'mtSemen': {
					let procedure = this.$t(this.cart.items[item].donor_data.material.fitness_for_insemination);
					name = this.$t('reservation.mtSemen.product_name') + ' ' + procedure;
					break;
				}
				case 'mtCell': {
					name = this.$t('reservation.mtCell.product_name', {size: this.cart.items[item].donor_data.material.package_size})
					break;
				}
				default: {
					name = this.$t('reservation.product_no_material_name')
				}
			}
			return name;
		},

		validateFields() {
			Object.keys(this.validationTable).forEach(field => {
				this.validationTable["field"] = false;
				if(field == 'different_location') {
					let result = true;
					Object.keys(this.cart.items)
						.filter(e => this.cart.items[e].checked)
						.forEach(f => {
							let materialLocationName = this.cart.items[f].donor_data.material.location.city;
							let userLocationName = this.userData.location?.name || '';
							if(userLocationName && (userLocationName != materialLocationName)) {
								result = false;
							}
						})
					this.validationTable[field] = result;
				}
				if(this.validationOn) {
					switch(field) {
						case "pesel_gender": {
							if(this.userData.noPesel) {
								this.validationTable[field] = true;
							} else {
								let gender = this.userData.pesel[9] % 2 == 0 ? 'female' : 'male';
								this.validationTable[field] = (gender == this.userData.gender);
							}
							break;
						}
						case "pesel_birthdate": {
							let result = true;
							if(this.userData.birthdate.year && this.userData.birthdate.month && this.userData.birthdate.day && this.userData.pesel)
							result = this.userData.birthdate.year.toString().slice(2,4) == this.userData.pesel.slice(0,2) &&
									parseInt(this.userData.birthdate.month) == parseInt(this.userData.pesel.slice(2,4)) % 20 &&
									this.userData.birthdate.day == this.userData.pesel.slice(4,6);
							this.validationTable[field] = result;
							break;
						}
						case "birthdate": {
							this.validationTable[field] = this.userData.birthdate.year && this.userData.birthdate.month && this.userData.birthdate.day;
							break;
						}
						case "birthdate_adult": {
							let date = new Date(
								this.userData.birthdate.year,
								this.userData.birthdate.month-1,
								this.userData.birthdate.day
							);
							let now  = new Date();
							now.setFullYear(now.getFullYear() - 18);
							this.validationTable[field] = date.getTime() < now.getTime();
							break;
						}
						case "pesel": {
							let result = 0;
							const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
							for(let i = 0; i < 10; i++) {
								result += (weights[i] * this.userData.pesel[i]);
							}
							this.validationTable[field] = this.userData.noPesel || ((10 - result % 10) % 10 == this.userData.pesel[10]);
							break;
						}
						case "documentType":
						case "documentNumber":
						case "documentCountry": {
							this.validationTable[field] = !this.userData.noPesel || this.userData[field]
							break;
						}
						case "country_id": {
							this.validationTable[field] = this.userAddress.country_id
							break;
						}
						case "voivodeship_id": {
							this.validationTable[field] = this.userAddress.voivodeship_id
							break;
						}
						case "poviat": {
							this.validationTable[field] = this.userAddress.poviat
							break;
						}
						case "commune": {
							this.validationTable[field] = this.userAddress.commune
							break;
						}
						case "locality": {
							this.validationTable[field] = this.userAddress.locality
							break;
						}
						case "street": {
							this.validationTable[field] = this.userAddress.street
							break;
						}
						case "house_number": {
							this.validationTable[field] = this.userAddress.house_number
							break;
						}
						case "post_office": {
							this.validationTable[field] = this.userAddress.post_office
							break;
						}
						case "zip_code": {
							if(document.querySelector("#zip_code")) {
								this.validationTable[field] = this.formatPostalCode();
							} else {
								this.validationTable[field] = true;
							}
							break;
						}
						case "transport_consent": {
							if(this.isTransportNeeded) {
								this.validationTable[field] = this.transportConsent;
							} else {
								this.validationTable[field] = true;
							}
							break;
						}
						case "person_file_data":
						case "mt_request_error":
						case "patient_mt_person_create_error":
						case "person_file_create":
						case "person_data_dictionary_error": {
							this.validationTable[field] = true;
							break;
						}
						default: {
							this.validationTable[field] = !!this.userData[field];
						}
					}
				} else {
					return true;
				}
			})
		},

		validateConsent(code) {
			let consent = this.allConsents.find(c => c.code == code);
			let recipient_consent = this.recipientConsents[code];
			if(consent.reservation && this.validationOn){
				return recipient_consent.consent_value;
			} else {
				return true;
			}
		},

		parseConsent(consent) {
			let text = consent.content[this.$i18n.locale];
			let reg = new RegExp(/\[\[\[([a-z_]+?)?;(.+?)?\]\]\]/g);
			let match;
			const link_service = `https://bank.invicta.pl${ this.$t('bank_invicta_path.terms_and_conditions') }`;
			const link_legal = `/dashboard?selected=legal-information&_locale=${this.$i18n.locale}`;
            const link_info = `https://bank.invicta.pl${ this.$t('bank_invicta_path.gdpr_policy') }`;
			while ((match = reg.exec(text)) != null) {
				switch(match[1]) {
					case "link_terms": {
						text = text.replace(match[0], `<a id="link-legal-info" href="${link_legal}" class="link" target="_blank">${match[2]}</a>`)
						break;
					}
					case "link_service": {
						text = text.replace(match[0], `<a href="${link_service}" class="link" target="_blank">${match[2]}</a>`)
						break;
					}
					case "link_info": {
						text = text.replace(match[0], `<a href="${link_info}" class="link" target="_blank">${match[2]}</a>`)
						break;
					}
				}
			}
			return text + (consent.reservation ? '*' : '');
		},

		backToOrigin() {
			this.$router.push(this.$router.options.history.state.back);
		},

		onlyNumberKey(evt) {
			evt = (evt) ? evt : window.event;
			var charCode = (evt.which) ? evt.which : evt.keyCode;
			if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
				evt.preventDefault();
			} else {
				return true;
			}
		},

		onlyNumberPaste(evt) {
			let text = evt.clipboardData.getData('Text');
			let regex = new RegExp(/^[\d]+$/);

			if (!regex.test(text)) {
				evt.preventDefault();
			} else {
				return true;
			}
		},

		onlyPhoneKey(evt) {
			this.badPhoneNumber = false;
			evt = (evt) ? evt : window.event;
			var charCode = (evt.which) ? evt.which : evt.keyCode;
			if (
				(charCode > 31 && (charCode < 48 || charCode > 57)) &&
				(charCode !== 45) &&
				(charCode !== 43) &&
				(charCode !== 32)
			) {
				evt.preventDefault();
			} else {
				return true;
			}
		},

		sendUserData() {
			let payload = {
				first_name: this.userData.name,
				last_name: this.userData.lastname,
				phone_number: this.userData.phone,

				birth_date: `${this.userData.birthdate.year}-${this.userData.birthdate.month}-${this.userData.birthdate.day}`,

				citizenship: this.userData.citizenship,
				citizenship_name: this.dicts.citizenship.find(e => e.code == this.userData.citizenship).name,

				gender: this.userData.gender.toLowerCase(),
				pesel_number: this.userData.noPesel ? '' : this.userData.pesel,
				identity_doc_type: this.userData.noPesel ? this.userData.documentType : '',
				identity_doc_number: this.userData.noPesel ? this.userData.documentNumber : '',
				identity_doc_country: this.userData.noPesel ? this.dicts.country.find(f => f.code == this.userData.documentCountry).name : '',
				location_of_treatment: this.userData.location.code,
				location_of_treatment_id: this.userData.location.id,
				location_of_treatment_name: this.userData.location.name,
				is_pesel: !this.userData.noPesel,

				file_id: this.fileId && this.tabSelected != 'tab_other' ? this.fileId : null,
				create_person_file: !this.fileId || this.tabSelected == 'tab_other' ? true : false
			}
			return new Promise((resolve, reject) => {
				reservation.postUserData(payload)
				.then(result => {
					if(result.file_id) {
						this.fileId = result.file_id
					}
					if(result.personal_data_record_id) {
						this.personalDataRecordId = result.personal_data_record_id;
					}
					resolve(result);
				})
				.catch(error => {
					console.error(error);
					reject(error);
				})
			})
		},

		sendConsents() {
			return new Promise((resolve, reject) => {
				let payloadPost = Object.keys(this.recipientConsents).map(c => {
					return {
						code: c,
						consent_value: this.recipientConsents[c].consent_value,
						consent_valid: this.recipientConsents[c].consent_valid
					}
				}).filter(e => e.consent_valid === null);
				let payloadPut = Object.keys(this.recipientConsents).map(c => {
					return {
						code: c,
						consent_value: this.recipientConsents[c].consent_value,
						consent_valid: this.recipientConsents[c].consent_valid
					}
				}).filter(e => e.consent_valid !== null);
				Promise.all([
					reservation.postNewConsents(payloadPost, this.fileId),
					reservation.updateConsents(payloadPut, this.fileId)
				])
				.then(result => {
					resolve(result);
				})
				.catch(error => {
					console.error(error);
					reject(error);
				})
			})
		},

		sendUserAddress() {
			return new Promise((resolve, reject) => {
				let payload = {
					voivodeship_id: this.userAddress.country_id == 1 ? this.userAddress.voivodeship_id : 0,
					country_id: this.userAddress.country_id,
					poviat_id: this.userAddress.poviat_id || 0,
					commune_id: this.userAddress.commune_id || 0,
					locality: this.userAddress.locality,
					post_office: this.userAddress.post_office,
					zip_code: this.userAddress.zip_code,
					house_number: this.userAddress.house_number,
					apartment_number: this.userAddress.apartment_number,
					street: this.userAddress.street,
				}
				reservation.postUserAddress(payload, this.fileId)
				.then(result => {
					resolve(result);
				})
				.catch(error => {
					console.error(error);
					reject();
				})
			})
		},

		sendReservation(with_payment) {

			let checkedCartItemProductKeys = Object.keys(this.cart.items)
				.filter(item => this.cart.items[item].checked)

			let checkedMzCartItemProductKeys = Object.keys(this.cart.items)
				.filter(item => this.cart.items[item].checked && this.cart.items[item].mz_grant_file_id)

			let checkedCartItemProductKeysWithAcceptanceId = {}
				checkedCartItemProductKeys.forEach(id => {
					checkedCartItemProductKeysWithAcceptanceId[id] = this.getAcceptanceIdByItemId(id)
				})

			return new Promise((resolve, reject) => {
				reservation.postReservation(checkedCartItemProductKeysWithAcceptanceId, this.personalDataRecordId, with_payment)
				.then(result => {
					let hsqPushedItems = []
					Object.entries(result.reservation_ids).forEach(entry => {
						const [id, item] = entry;
						let wasPushedItemToHs = hsqPushedItems.includes(item)
						let price = this.cart.items[item].price
						let quantity = this.cart.items[item].quantity
						let mz_grant = this.cart.items[item].mz_grant_file_id ? true : false
						let nickname = this.cart.items[item].donor_data.donor_process.donor_nickname
						let hsqEventProperties = {
							price: price,
							donor_id: nickname,
							quantity: mz_grant ? ( wasPushedItemToHs ? quantity - 1 : 1 ) : quantity,
							product_key: item,
							total_price: mz_grant ? ( wasPushedItemToHs ? price * ( quantity - 1 ) : 0 ) : price * quantity,
							product_name: this.getProductName(item),
							material_type: this.cart.items[item].material_type,
							material_location: this.cart.items[item].donor_data.material.location.city,
							reservation_id: id,
							order_id: result.order_id,
							profile_public_id: result.profile_public_id,
							user_location: this.userData.location?.name,
							user_name: this.oidcUser.name,
							user_email: this.oidcUser.email,
							with_payment: mz_grant && !wasPushedItemToHs ? "false" : with_payment.toString(),
							mz_grant: mz_grant && !wasPushedItemToHs ? "true" : "false"
						}

						window._hsq = window._hsq || []
						window._hsq.push(['identify',{ email: this.oidcUser.email }])
						window._hsq.push([
							'trackCustomBehavioralEvent',
							{
								name: process.env.VUE_APP_FAKE_DOOR_HS_RESERVATION_EVENT_NAME,
								properties: hsqEventProperties
							},
						]);

						this.$gtag.event('donor', {
							'donor_product_key': item,
							'donor_material_type': this.cart.items[item].material_type,
							'donor_event_value': 'sended reservation'
						})
						hsqPushedItems.push(item)

						if(this.cart.items[item].checked) {
							reservation.deleteItemByKey(item)
							.then(response => {
								this.$store.dispatch('cartModule/fetchCart')
								resolve(response)
							})
							.catch(error => {
								console.error(error)
								reject(error)
							})
						}
					})

					this.$store.dispatch('reservationModule/clearChecked')
					this.$store.dispatch('cartModule/clearItems')

					if (checkedMzCartItemProductKeys.length) {
						this.$store.dispatch('fileMTModule/fetchFiles')
					}

					resolve(result);
				})
				.catch(error => {
					if (error.response) {
						console.error(error.response.data);
						if (error.response.data.error_key == 'NO_PHENOTYPE') {
							this.$root.$alertMessageRef.showAlert(
								this.$t(`alert.RESERVATION_SAVE_FAILURE_DUE_NO_PHENOTYPE.title`),
								null,
								{
									link: false,
									action: false,
									timeout: false,
									color: 'rgba(32, 30, 76, 1)',
									backgroundColor: 'rgba(232, 69, 69, 0.2)',
									borderColor: 'rgba(232, 69, 69, 1)',
									icon: 'mdi-close-circle-outline',
									iconColor: 'rgba(232, 69, 69, 1)',
									width: '600px'
								}
							);
						} else if(error.response.data.error_key == "CREATE_DDC_RESERVATION") {
							this.reason = this.$t(`reservation.screen_failure_material_unavailable_text`)
						} 
					}
					reject(error);
				})
			})
		},

		saveReservation(with_payment) {
			window.scrollTo({top: 0, behavior: 'smooth'});
			this.validationOn = true;
			this.validateFields();
			this.$nextTick(() => {
				this.screen_ready = false;
				this.reason = null;
				let errors = document.querySelector('.validation');
				if(errors) {
					errors.scrollIntoView({behavior: 'smooth', block: 'center'});
				} else {
					this.loading_save = true;
					
					this.sendUserData()
					.then(() => {
						// we always have file_id here, proceed
						// with other posts
						Promise.all([
							this.sendUserAddress(),
							this.sendConsents()
						])
						.then(() => {
							// all relevant data was sent,
							// time for grand finale
							this.sendReservation(with_payment)
							.then((result) => {
								if (this.getPayment && result.reservation_ids) {
									reservation.getPaymentUrl(
										Object.keys(result.reservation_ids),
										reservation.getPaymentReturnUrlWithParameters(Object.keys(result.reservation_ids), this.$i18n.locale)
									)
									.then(result => {
										if (result.payment_url && result.already_paid != true) {
											window.location.href = result.payment_url
										}
									})
									.catch(error => {
										if (error.response) {
											console.error(error.response.data)
										} else {
											console.error("PAYMENT ERROR:",error)
										}
									})
								}
								this.success = true;
								this.screen_ready = true;
							})
							.catch(error => {
								console.error(error);
								this.success = false;
								this.screen_ready = true;
								// Component reload hack
								this.mtFilesSelectKey = Math.random()
							})
							.finally(() => {
								this.loading_save = false;
								this.currentSlot = 3;
							})
						})
					})
					.catch(error => {
						this.loading_save = false;
						if(error.response.status == 422) {
							switch(error.response.data.error_key) {
								case "PERSON_FILE_DATA": {
									this.validationTable["person_file_data"] = false;
									break;
								}
								case "MT_REQUEST_ERROR": {
									this.validationTable["mt_request_error"] = false;
									break;
								}
								case "PATIENT_MT_PERSON_CREATE_ERROR": {
									this.validationTable["patient_mt_person_create_error"] = false;
									break;
								}
								case "PERSON_FILE_CREATE": {
									this.validationTable["person_file_create"] = false;
									break;
								}
								case "PERSON_DATA_DICTIONARY_ERROR": {
									this.validationTable["person_data_dictionary_error"] = false;
									// Whole dict error, like omega bad, not my fault
									break;
								}
								case "BIRTH_NOT_VALID_ERROR": {
									this.validationTable["birthdate"] = false;
									break;
								}
								case "PESEL_NOT_VALID_ERROR": {
									this.validationTable["pesel"] = false;
									break;
								}
								case "PESEL_BIRTH_NOT_EQUAL_ERROR": {
									this.validationTable["pesel_birthdate"] = false;
									break;
								}
								case "INVALID_GENDER": {
									this.validationTable["pesel_gender"] = false;
									break;
								}
								case "INVALID_DICT_VALUE_ERROR": {
									const error_field = error.response.data.additional_data.field.split(' ').join('_').toLowerCase();
									switch(error_field) {
										case "identity_doc_type": {
											this.validationTable["documentType"] = false;
											break;
										}
										case "identity_doc_country": {
											this.validationTable["documentCountry"] = false;
											break;
										}
										default: {
											this.validationTable[error_field] = false;
										}
									}
									break;
								}
								default: {
									break;
								}
							}
						}
					})
					.finally(() => {
						const errors = document.querySelector('.validation');
						if(errors) {
							errors.scrollIntoView({behavior: 'smooth', block: 'center'});
						}
					})
				}
			})	
		},

		redirect() {
			this.$router.push(`dashboard?selected=reservations&_locale=${this.$i18n.locale}`);
		},

		onConsentLoad() {
			document.getElementById("link-legal-info").addEventListener('click', 
				(e) => {
					this.legalInformationDialogVisible = true;
					e.preventDefault();
				}
			);
		},

		showTransportConditionsDialog(e) {
			this.transportConditionsDialogVisible = true;
			e.preventDefault();
		},
	}
}

</script>

<style lang="scss" scoped>
@import "../../node_modules/design-system-ui-components/dist/all.min.css";

.close {
  cursor: pointer;
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

// DEBUG
:deep(.selectfield--item) {
	width: 538px;
	.selectfield--item-selected {
		color: red;
	}
}
:deep(.selectfield--menu) {
	width: 100%;
}

.select-mock {
	border: 1px solid rgb(230, 230, 231);
	padding: 8px 16px;
	border-radius: 4px;
}
:deep(svg.icon-expand-more) {
	color: $ds-primary !important;
}
:deep(.selectfield--selection>svg.icon-close) {
	color: $ds-primary !important;
}
.section-header {
	@include nsb-gentium-bold;
    font-size: 23px;
}

.chained-dialog-component {
	margin: 0 auto;
}
.reservation-screen-view {
	position: relative;
}
.table-wrapper {
	box-shadow: 0px 0px 1px 1px $ds-background-1;
	width: 100%;
}

.tab-group {
	max-width: unset;
	min-width: 100%;
}
.select-personal-data {
	width: 100%;
}
.select-location-data {
	width: 100%;
}
/* WIP */
.option-disabled {
	color: magenta;
	padding: 0;
	margin: 0;
	user-select: none;
	pointer-events: none;
	&:hover {
		all: unset;
	}
}
/* END WIP */
.disabled,input:read-only {
	user-select: none;
	pointer-events: none;
	position: relative;
	background-color: $nsb-disabled;
	color: $nsb-disabled-text;
	font-weight: bold;
	cursor: default;
}
label.disabled {
	color: $ds-primary-6 !important;
	padding-left: 32px !important;
	font-weight: normal;
	background: none;
}

.file_loading {
	
	width: 588px;
	background: white;
	margin: 0 auto;
	position: relative; 
}

.reservation-screen-wrapper {
	// min-height: calc(100vh - 130px);
	max-width: 588px;
	background: white;
	margin: 0 auto;
	position: relative;
	v-btn {
		@include nsb-font-header-bold;
	}
	.long {
		width: 100%;
	}
}
.reservation-screen-header {
	@include nsb-gentium-bold;
    font-size: 28px;
    line-height: 36px;
}
.reservation-screen-subheader {
	@include nsb-font-container;
	font-size: 14px;
	line-height: 21px;
	.id {
		@include nsb-inter-regular;
		border-radius: 12px;
		background-color: $ds-primary-9;
		font-size: 14px;
		line-height: 21px;
	}
	.reservation-label {
		@include nsb-font-container;
		font-size: 14px;
		line-height: 21px;
	}
}
.reservation-screen-data {
	width: calc(100% - 32px);
	.data-label {
		@include nsb-font-container-medium;
		font-size: 12px;
		line-height: 18px;
        color: $ds-primary-3;
	}
	.data-value {
		@include nsb-font-container;
		font-size: 14px;
		line-height: 21px;
	}
	.data-subvalue {
		font-size: 12px;
		line-height: 18px;
        color: $ds-primary-3;
	}
}
.reservation-screen-text {
	@include nsb-font-container;
	font-size: 16px;
	line-height: 24px;
}
.thank-you-screen {
	max-width: calc(588px + 48px);
	top: 50%;
}

.consent-wrapper {
	.consent {
		.container {
			@include nsb-font-container;
			font-size: 12px !important;
			line-height: 18px;
		}
	}
}

.input-wrapper , .document-wrapper{
	position: relative;
	width: 100%;
	.input-label {
		@include nsb-font-container;
		font-size: 14px;
		line-height: 21px;
	}
	.input {
		@include nsb-font-input;
		font-size: 14px !important;
		height: 40px;
		width: 100%;
		padding: 0px 16px;
		outline: none;
	}
	.select-wrapper {
		position: relative;
		.select {
			@include nsb-font-input;
			font-size: 14px !important;
			height: 40px;
			width: 100%;
			padding: 0px 16px;
			outline: none;
			option:disabled {
				color: $nsb-disabled-text;
			}
		}
		.chevron {
			position: absolute;
			right: 16px;
			top: 8px;
			pointer-events: none;
		}
	}
	.date-wrapper {
		position: relative;
		.select {
			@include nsb-font-input;
			font-size: 14px !important;
			// background-color: $nsb-disabled;
			height: 40px;
			width: 150px;
			min-width: 80px;
			padding: 0px 16px;
			outline: none;
			text-overflow: ellipsis;
			option:disabled {
				color: $nsb-disabled-text;
			}
		}
		.long {
			width: 100%;
		}
		.chevron {
			position: absolute;
			right: 16px;
			top: 8px;
			pointer-events: none;
		}
	}
}

#noPesel {
	display: none;
}
.validation {
  @include nsb-validation;
}
.information {
	@include nsb-validation;
	color: $ds-primary;
}

.progress {
	// position: fixed;
	// top: 50vh;
	// left: 50vw;
	// transform: translate(-50%,-50%);
}

.progress_summary {
	position: absolute;
	top: 60%;
	left: 50%;
	transform: translate(-50%,-50%);
}

:deep(.link) {
	text-decoration: underline !important;
}

.reservation_disabled {
	position: relative;
	// filter: grayscale(1);
	// background: rgba(0,0,0,0.05);
	pointer-events: none;
	user-select: none;
}
.reservation_disabled_info {
	@include nsb-font-header-bold;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%,-50%);
	font-size: 32px;
	color: red !important;
}
.option-top-label {
	color: $ds-primary-100;
	font-size: 10px;
}
.transport-price {
	color: $ds-icon-success;
	font-size: 16px;
}
.transport-time-info {
	color: $ds-primary-100;
	font-size: 12px;
	span {
		color: $ds-primary;
	}
}
.info-header-wrapper {
    .info-title {
      @include nsb-gentium-bold;
      font-size: 28px;
      line-height: 40px;
    }
    .info-subtitle {
      @include nsb-font-header;
      font-size: 16px;
      line-height: 26px;
    }
    .info-bold {
      @include nsb-font-header-bold;
      font-size: 16px;
      line-height: 26px;
    }
  }
  .info-wrapper {
    background: white;
    max-width: 588px;
    margin: 64px auto;
    margin-top: 32px;
  }
  .info-body-wrapper {
    margin-left: 0px;
    @include nsb-font-header;
    font-size: 16px;
    line-height: 26px;
  }
  .info-button {
    @include nsb-font-header-bold;
    font-size: 14px;
    line-height: 21px;
    color: white;
    width: 100%;
  }

  @media (min-width: 960px) {
  .invicta {
    background: transparent;
  }
    .info-wrapper {
      margin: 120px auto;
    }
    .info-header-wrapper {
		display: grid;
		grid-template-columns: repeat(80px, 1fr);
		grid-template-rows: repeat(5, auto);
		grid-column-gap: 0px;
		grid-row-gap: 0px;
    }

	.div-1 { grid-area: 1 / 1 / 2 / 2; }
	.div-2 { grid-area: 1 / 2 / 2 / 3; }
	.div-3 { grid-area: 2 / 2 / 3 / 3; }
	.div-4 { grid-area: 3 / 2 / 4 / 3; }
	.div-5 { grid-area: 4 / 2 / 5 / 3; }

    .info-body-wrapper {
      margin-left: 80px;
    }
    .info-button {
      width: auto;
    }

	.thank-you-screen {
		margin: 25% auto;
	}
	.reservation-screen-header {
		font-size: 40px;
		line-height: 52px;
	}
	.reservation-screen-wrapper {
		min-height: unset;
	}
	.tab-group {
		max-width: 588px;
		min-width: 588px;
	}
	.order-summary {
		max-height: 405px;
		position: sticky;
		top: 10px;
		bottom: 400px;
	}
}
/* The container */
.container {
  display: block;
  position: relative;
  padding-left: 24px;
  cursor: pointer;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  box-sizing: border-box;
  border: 1px solid #869AB7;
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  width: 16px;
  background-color: white;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: $nsb-disabled;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: $ds-primary;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 1px 1px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

// custom scroll for dropdown
// maybe should be global
/* width */
::-webkit-scrollbar {
  width: 5px;
}

/* Track */
::-webkit-scrollbar-track {
  background: $nsb-color-3;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: $nsb-color-4;
  border-radius: 4px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: $nsb-color-4;
}

</style>